import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Link } from 'react-router-dom';
import { BarChart3, Gauge, MessageSquare, Smartphone, Building2, Users, Code2 } from 'lucide-react';

const FinancialIntelligenceAssistant = () => {
  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="relative pt-32 pb-20 px-6 bg-gradient-to-br from-[#060D1B] via-[#0A1628] to-[#0d1f3c]">
        <div className="max-w-[1200px] mx-auto relative z-10">
          <div className="text-center">
            <div className="inline-block px-3 py-1 bg-[#4F46E5]/20 text-[#4F46E5] text-xs font-semibold uppercase tracking-wider rounded-full mb-4">
              Consumer & SME Layer
            </div>
            <h1 className="text-h1 text-white mb-6">Financial Intelligence Assistant</h1>
            <p className="text-body-lg text-[#6B7280] max-w-3xl mx-auto mb-8">
              A personalised AI co-pilot for consumers and SMEs — embedded in your channels, built on your data.
            </p>
            <Button 
              asChild 
              size="lg" 
              className="bg-[#1A56DB] hover:bg-[#1A56DB]/90 text-white font-semibold text-lg px-8 h-14 btn-hover-lift shadow-lg shadow-[#1A56DB]/30"
            >
              <Link to="/contact">Request Demo</Link>
            </Button>
          </div>
        </div>
      </section>

      {/* The Problem Section */}
      <section className="section-padding px-6 bg-[#EFF6FF]">
        <div className="max-w-[1200px] mx-auto">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-h3 text-[#0A1628] mb-4">
              Your Customers Have No Intelligent Financial Layer
            </h2>
            <p className="text-body-lg text-[#6B7280] leading-relaxed">
              Consumers and SMEs navigate their financial lives with static statements, disconnected products, and no proactive guidance. Banks have the data. What's missing is the intelligence layer that makes it actionable — for the customer.
            </p>
          </div>
        </div>
      </section>

      {/* Three Feature Cards */}
      <section className="section-padding px-6 bg-white">
        <div className="max-w-[1200px] mx-auto">
          <div className="grid md:grid-cols-3 gap-8">
            <Card className="bg-[#EFF6FF] border-[#D1D5DB] rounded-xl shadow-sm hover:shadow-lg transition-shadow">
              <CardContent className="p-8">
                <div className="p-3 bg-[#4F46E5]/10 rounded-lg w-fit mb-4">
                  <BarChart3 className="text-[#4F46E5]" size={28} />
                </div>
                <h3 className="text-xl font-bold text-[#0A1628] mb-3">Spend & Income Intelligence</h3>
                <p className="text-[#6B7280] text-sm leading-relaxed">
                  Real-time categorisation of transactions with behavioural pattern analysis and anomaly alerts.
                </p>
              </CardContent>
            </Card>

            <Card className="bg-[#EFF6FF] border-[#D1D5DB] rounded-xl shadow-sm hover:shadow-lg transition-shadow">
              <CardContent className="p-8">
                <div className="p-3 bg-[#4F46E5]/10 rounded-lg w-fit mb-4">
                  <Gauge className="text-[#4F46E5]" size={28} />
                </div>
                <h3 className="text-xl font-bold text-[#0A1628] mb-3">Credit Readiness Score</h3>
                <p className="text-[#6B7280] text-sm leading-relaxed">
                  Dynamic creditworthiness assessment using live behavioural data — not static bureau scores.
                </p>
              </CardContent>
            </Card>

            <Card className="bg-[#EFF6FF] border-[#D1D5DB] rounded-xl shadow-sm hover:shadow-lg transition-shadow">
              <CardContent className="p-8">
                <div className="p-3 bg-[#4F46E5]/10 rounded-lg w-fit mb-4">
                  <MessageSquare className="text-[#4F46E5]" size={28} />
                </div>
                <h3 className="text-xl font-bold text-[#0A1628] mb-3">Conversational Co-pilot</h3>
                <p className="text-[#6B7280] text-sm leading-relaxed">
                  Natural language interface for financial queries, loan guidance, and proactive product recommendations — through your app or WhatsApp.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Channel Integration Section */}
      <section className="section-padding px-6 bg-[#0A1628]">
        <div className="max-w-[1200px] mx-auto text-center">
          <h2 className="text-h2 text-white mb-6">
            Embedded in Your Channels
          </h2>
          <p className="text-body-lg text-[#6B7280] max-w-3xl mx-auto mb-8">
            Financial Intelligence Assistant is deployed through your existing customer touchpoints. No new apps. No separate login. Your brand, your data, our intelligence.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            {[
              { icon: Smartphone, label: 'Mobile App' },
              { icon: MessageSquare, label: 'WhatsApp' },
              { icon: Building2, label: 'Internet Banking' },
              { icon: Code2, label: 'API Integration' }
            ].map((channel, idx) => (
              <div 
                key={idx} 
                className="flex items-center gap-2 px-5 py-3 glass-card text-white font-medium text-sm"
              >
                <channel.icon size={18} className="text-[#4F46E5]" />
                {channel.label}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Who It's For Section */}
      <section className="section-padding px-6 bg-[#EFF6FF]">
        <div className="max-w-[1200px] mx-auto">
          <h2 className="text-h2 text-[#0A1628] mb-10 text-center">Who It's For</h2>
          
          <div className="grid md:grid-cols-2 gap-8">
            <Card className="bg-white border-[#D1D5DB] rounded-xl shadow-sm hover:shadow-lg transition-shadow">
              <CardContent className="p-8">
                <div className="p-3 bg-[#1A56DB]/10 rounded-lg w-fit mb-4">
                  <Building2 className="text-[#1A56DB]" size={28} />
                </div>
                <h3 className="text-xl font-bold text-[#0A1628] mb-3">Retail Banks & NBFCs</h3>
                <p className="text-[#6B7280] text-sm leading-relaxed">
                  Deliver personalised financial guidance at scale. Increase cross-sell relevance and reduce churn with customers who feel understood.
                </p>
              </CardContent>
            </Card>

            <Card className="bg-white border-[#D1D5DB] rounded-xl shadow-sm hover:shadow-lg transition-shadow">
              <CardContent className="p-8">
                <div className="p-3 bg-[#1A56DB]/10 rounded-lg w-fit mb-4">
                  <Users className="text-[#1A56DB]" size={28} />
                </div>
                <h3 className="text-xl font-bold text-[#0A1628] mb-3">Consumers & SMEs</h3>
                <p className="text-[#6B7280] text-sm leading-relaxed">
                  Know where your money is going, understand your credit position, and get proactive advice — without switching apps.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section-padding px-6 bg-gradient-to-r from-[#0A1628] to-[#1A56DB]">
        <div className="max-w-[1200px] mx-auto text-center">
          <h2 className="text-h2 text-white mb-6">
            Empower Your Customers
          </h2>
          <p className="text-body-lg text-white/80 mb-8 max-w-2xl mx-auto">
            See how Financial Intelligence Assistant can transform customer engagement
          </p>
          <Button 
            asChild 
            size="lg" 
            className="bg-white hover:bg-white/90 text-[#0A1628] font-bold text-lg px-10 h-14 btn-hover-lift shadow-xl"
          >
            <Link to="/contact">Request a Demo</Link>
          </Button>
        </div>
      </section>
    </div>
  );
};

export default FinancialIntelligenceAssistant;
